import { authModule } from './auth-module';
import { scopesForUrl } from '@/http/scope-map';

/**
 * RUM (raptor) token acquisition + cache.
 *
 * Several EY APIM backends (portalapps, role-scr-mgmt, portaladmin,
 * incidentstatus, myteams) require BOTH an Azure AD bearer token AND a RUM
 * token in a `Raptor-Authorization` header. Legacy supplies the latter via
 * `RumInterceptor` (`raptor-shell-ui/.../rum.interceptor.ts`), which calls
 * `RumService.getRumToken(clientId, scopes)`. `RumService` caches the token in
 * `tokenCache[clientId][scope]` until ~60s before its JWT `exp`.
 *
 * This module is the single source of RUM tokens for the React shell, shared
 * by:
 *   - the `EY_RAPToR_getRumToken` global (legacy MFE widget requests), and
 *   - `http()` (shell requests to RUM-protected APIM endpoints).
 *
 * Keeping one cache + single-flight here avoids duplicate `/rum/.../token`
 * round-trips across both callers.
 */

// Legacy `RumService` keeps `tokenCache[clientId][scope]` and serves a cached
// raptorToken until ~60s before its JWT `exp`.
const rumTokenCache = new Map<string, string>();
const rumTokenInflight = new Map<string, Promise<string>>();

/** Clear all cached RUM tokens — called on client change (legacy clearRumTokenCache). */
export function clearRumTokenCache(): void {
  rumTokenCache.clear();
  rumTokenInflight.clear();
}

/**
 * JWT-exp expiry check (legacy rum.service.ts `checkTokenExpiry`): decode the
 * payload, treat as expired 60s before the real `exp` so a token never reaches
 * an API at the last moment. Malformed/exp-less tokens are treated as expired.
 */
function isRumTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { exp?: number };
    if (typeof claims.exp !== 'number') return true;
    return claims.exp - 60 <= Date.now() / 1000;
  } catch {
    return true;
  }
}

/**
 * Acquire a RUM token for `clientId` + `scopes` (default scope `'Portal'`).
 * Returns the cached token while valid; coalesces concurrent misses onto a
 * single `/rum/.../token` request.
 */
export async function getRumToken(
  clientId: string | undefined,
  scopes?: string | string[],
): Promise<string> {
  const scopeParam = (Array.isArray(scopes) ? scopes[0] : scopes) || 'Portal';

  const cacheKey = `${clientId ?? ''}|${scopeParam}`;
  const cached = rumTokenCache.get(cacheKey);
  if (cached && !isRumTokenExpired(cached)) return cached;
  const pendingReq = rumTokenInflight.get(cacheKey);
  if (pendingReq) return pendingReq;

  const apim = import.meta.env.VITE_RAPTOR_APIM_BASE_URL;
  const tokenServicePath = import.meta.env.VITE_TOKEN_SERVICE_PATH;
  const url = new URL(`${apim}${tokenServicePath}/v1/authorization/token`);
  url.searchParams.set('scopes', scopeParam);
  if (clientId) url.searchParams.set('clientId', clientId);

  const fetchToken = (async () => {
    const accessToken = await authModule.getTokenAsync(scopesForUrl(url.toString()));
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const text = await res.text();
    let parsed: unknown = text;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        /* response is not JSON */
      }
    }

    if (!res.ok) {
      throw new Error(`RUM token request failed: ${res.status} ${res.statusText}`);
    }

    const response = parsed as {
      raptorToken?: unknown;
      RaptorToken?: unknown;
      token?: unknown;
      accessToken?: unknown;
    };
    const raptorToken =
      response.raptorToken ?? response.RaptorToken ?? response.token ?? response.accessToken;
    const token =
      typeof raptorToken === 'string' ? raptorToken : typeof parsed === 'string' ? parsed : '';
    if (token) rumTokenCache.set(cacheKey, token);
    return token;
  })().finally(() => {
    rumTokenInflight.delete(cacheKey);
  });

  rumTokenInflight.set(cacheKey, fetchToken);
  return fetchToken;
}
